import { useEffect, useMemo, useRef, useState } from 'react';
import type { CurrencyAdjustRequest } from '@shared/types';
import { adjustCurrency } from '../services/users';
import { ApiError } from '../services/api';
import { useToast } from '../stores/toastStore';
import Form, { Field } from './Form';
import { useForm } from '../hooks/useForm';

export interface CurrencyModalUser {
  id: string;
  coins: number;
  gems: number;
  energy: number;
  maxEnergy: number;
}

interface CurrencyModalProps {
  open: boolean;
  onClose: () => void;
  user: CurrencyModalUser;
  /** Fires after a successful adjustment so parents can refetch. */
  onSuccess: () => void;
}

interface CurrencyFormValues {
  coinsDelta: string; // raw input text — parsed on submit
  gemsDelta: string;
  energyDelta: string;
  reason: string;
}

const EMPTY: CurrencyFormValues = {
  coinsDelta: '',
  gemsDelta: '',
  energyDelta: '',
  reason: '',
};

/**
 * Parse a signed-integer delta from a raw text input. Empty / whitespace is
 * treated as 0 (no change). Returns `null` when the input is present but
 * unparseable so the caller can mark the field invalid.
 */
function parseDelta(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '+') return 0;
  if (!/^[+-]?\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

/**
 * Admin currency adjustment modal. Destructive-style action — every submission
 * writes a Transaction + AuditLog entry server-side, so we enforce:
 *   - at least one delta must be non-zero
 *   - reason required, min 3 chars (matches server Zod schema)
 *
 * Previews clamp the energy balance to [0, maxEnergy] and floor coins/gems at
 * 0 to mirror the server's atomic pipeline — gives admins a truthful "new
 * balance" number before they commit.
 */
export default function CurrencyModal({
  open,
  onClose,
  user,
  onSuccess,
}: CurrencyModalProps) {
  const { showToast } = useToast();
  const form = useForm<CurrencyFormValues>(EMPTY);
  const { values, errors, setField, setError, reset } = form;

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Reset fields every time the modal is (re)opened for a user so stale
  // drafts from a previous open don't bleed across sessions.
  const lastOpenRef = useRef(false);
  useEffect(() => {
    if (open && !lastOpenRef.current) {
      reset(EMPTY);
      setServerError(null);
    }
    lastOpenRef.current = open;
  }, [open, reset]);

  // Escape closes (but not while submitting).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, submitting, onClose]);

  const parsed = useMemo(() => {
    return {
      coins: parseDelta(values.coinsDelta),
      gems: parseDelta(values.gemsDelta),
      energy: parseDelta(values.energyDelta),
    };
  }, [values.coinsDelta, values.gemsDelta, values.energyDelta]);

  const preview = useMemo(() => {
    const coinsDelta = parsed.coins ?? 0;
    const gemsDelta = parsed.gems ?? 0;
    const energyDelta = parsed.energy ?? 0;
    const newCoins = Math.max(0, user.coins + coinsDelta);
    const newGems = Math.max(0, user.gems + gemsDelta);
    const newEnergy = Math.min(
      user.maxEnergy,
      Math.max(0, user.energy + energyDelta),
    );
    return { newCoins, newGems, newEnergy };
  }, [parsed, user.coins, user.gems, user.energy, user.maxEnergy]);

  async function handleSubmit() {
    let hasFieldError = false;

    if (parsed.coins === null) {
      setError('coinsDelta', 'Must be an integer (e.g. 100 or -50)');
      hasFieldError = true;
    }
    if (parsed.gems === null) {
      setError('gemsDelta', 'Must be an integer (e.g. 100 or -50)');
      hasFieldError = true;
    }
    if (parsed.energy === null) {
      setError('energyDelta', 'Must be an integer (e.g. 5 or -3)');
      hasFieldError = true;
    }

    const coinsDelta = parsed.coins ?? 0;
    const gemsDelta = parsed.gems ?? 0;
    const energyDelta = parsed.energy ?? 0;

    if (
      !hasFieldError &&
      coinsDelta === 0 &&
      gemsDelta === 0 &&
      energyDelta === 0
    ) {
      // Attach to coinsDelta to match the server's refinement path.
      setError(
        'coinsDelta',
        'At least one of coins, gems, or energy must be non-zero',
      );
      hasFieldError = true;
    }

    const reason = values.reason.trim();
    if (reason.length < 3) {
      setError('reason', 'Reason is required (min 3 characters)');
      hasFieldError = true;
    }

    if (hasFieldError) return;

    const payload: CurrencyAdjustRequest = {
      reason,
      ...(coinsDelta !== 0 ? { coinsDelta } : {}),
      ...(gemsDelta !== 0 ? { gemsDelta } : {}),
      ...(energyDelta !== 0 ? { energyDelta } : {}),
    };

    setSubmitting(true);
    setServerError(null);
    try {
      await adjustCurrency(user.id, payload);
      showToast('Balances adjusted', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.error}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Failed to adjust balances';
      setServerError(message);
      showToast('Failed to adjust balances', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="currency-modal-title"
      data-testid="currency-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        data-testid="currency-modal-backdrop"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-4">
        <div>
          <h2
            id="currency-modal-title"
            className="text-base font-semibold text-slate-900"
          >
            Adjust balances
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Positive numbers credit the user; negative numbers debit. Energy is
            clamped to the user&rsquo;s max.
          </p>
        </div>

        <Form
          onSubmit={handleSubmit}
          testId="currency-modal-form"
          className="space-y-3"
        >
          <Field
            label="Coins delta"
            htmlFor="currency-coins"
            error={errors.coinsDelta ?? null}
            hint={`Current: ${user.coins.toLocaleString()} → New: ${preview.newCoins.toLocaleString()}`}
          >
            <input
              id="currency-coins"
              type="text"
              inputMode="numeric"
              value={values.coinsDelta}
              onChange={(e) => setField('coinsDelta', e.target.value)}
              placeholder="e.g. 100 or -50"
              data-testid="currency-coins-input"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              disabled={submitting}
            />
          </Field>

          <Field
            label="Gems delta"
            htmlFor="currency-gems"
            error={errors.gemsDelta ?? null}
            hint={`Current: ${user.gems.toLocaleString()} → New: ${preview.newGems.toLocaleString()}`}
          >
            <input
              id="currency-gems"
              type="text"
              inputMode="numeric"
              value={values.gemsDelta}
              onChange={(e) => setField('gemsDelta', e.target.value)}
              placeholder="e.g. 10 or -5"
              data-testid="currency-gems-input"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              disabled={submitting}
            />
          </Field>

          <Field
            label="Energy delta"
            htmlFor="currency-energy"
            error={errors.energyDelta ?? null}
            hint={`Current: ${user.energy}/${user.maxEnergy} → New: ${preview.newEnergy}/${user.maxEnergy}`}
          >
            <input
              id="currency-energy"
              type="text"
              inputMode="numeric"
              value={values.energyDelta}
              onChange={(e) => setField('energyDelta', e.target.value)}
              placeholder="e.g. 3 or -1"
              data-testid="currency-energy-input"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              disabled={submitting}
            />
          </Field>

          <Field
            label="Reason"
            htmlFor="currency-reason"
            error={errors.reason ?? null}
            required
            hint="Shown in the audit log. Minimum 3 characters."
          >
            <textarea
              id="currency-reason"
              value={values.reason}
              onChange={(e) => setField('reason', e.target.value)}
              rows={2}
              placeholder="e.g. Compensation for bug report #ABC"
              data-testid="currency-reason-input"
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              disabled={submitting}
            />
          </Field>

          {serverError && (
            <p
              role="alert"
              data-testid="currency-modal-server-error"
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
            >
              {serverError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                if (!submitting) onClose();
              }}
              disabled={submitting}
              data-testid="currency-modal-cancel"
              className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="currency-modal-submit"
              className="text-sm rounded px-3 py-1.5 bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Apply adjustment'}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
